import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthenticationService } from '../services/authentication.service';
import { User } from '../model/user';
import { ToastrService } from 'ngx-toastr';
import { RecaptchaModule } from 'ng-recaptcha';
import { environment } from '../../environments/environment';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { setTempPasswordExpiration } from '../helpers/temp-password.util';

@Component({
  selector: 'app-create-account',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, RecaptchaModule],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.scss',
})
export class CreateAccountComponent {
  captchaResponse: string | null = null;
  captchaResolved: boolean = false;
  isSubmitting = false;
  recaptchaSiteKey: string = '';
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  emailRegx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  protected states: { code: string; name: string }[] = [];

  constructor(
    public router: Router,
    private authService: AuthenticationService,
    private toastr: ToastrService,
    private commonUtility: CommonUtilityServiceService
  ) {
    this.recaptchaSiteKey = environment.recaptchaSiteKey;
  }

  protected createAccount = new FormGroup(
    {
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(this.emailRegx),
      ]),
      phone: new FormControl('', [Validators.required]),
      usePhoneForSms: new FormControl(false),
      location: new FormGroup({
        street: new FormControl('', [Validators.required]),
        city: new FormControl('', [Validators.required]),
        state: new FormControl('', [Validators.required]),
        zipcode: new FormControl('', [Validators.required]),
      }),
      password: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(this.passwordRegx),
      ]),
    },
    { validators: this.customPasswordMatching.bind(this) }
  );

  ngOnInit() {
    this.states = Array.from(
      this.commonUtility.stateMap,
      ([code, name]) => ({ code, name })
    );
  }

  public customPasswordMatching(
    control: AbstractControl
  ): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    return password === confirmPassword
      ? null
      : { passwordMismatchError: true };
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  trimPassword() {
    let password = this.createAccount.get('password')?.value;
    if (password) {
      this.createAccount.patchValue({
        password: password.trim(),
      });
    }
  }

  trimConfirmPassword() {
    let password = this.createAccount.get('confirmPassword')?.value;
    if (password) {
      this.createAccount.patchValue({
        confirmPassword: password.trim(),
      });
    }
  }

  cancel() {
    this.router.navigate(['login']);
  }

  register() {
    if (this.createAccount.invalid) {
      this.createAccount.markAllAsTouched();
    } else if (!this.captchaResponse) {
      this.toastr.error(
        'Please verify that you are not a robot by clicking on the reCAPTCHA checkbox.'
      );
    } else if (this.createAccount.valid && this.captchaResponse) {
      this.isSubmitting = true;
      let user = <User>this.createAccount.value;
      this.authService.register(user).subscribe({
        next: (data) => {
          this.isSubmitting = false;
          if (data.status === 'SUCCESS') {
            // Registration doesn't sign the user in — the only thing the SPA
            // needed off this response was the temporary password's expiration,
            // so record just that instead of the whole envelope.
            setTempPasswordExpiration(data?.data?.tempPasswordExpiration);
            this.router.navigate(['thanks']);
          } else {
            this.toastr.error(data.message);
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        }
      });
    }
  }

  onCaptchaResolved(captchaResponse: any) {
    this.captchaResponse = captchaResponse;
  }
}
